import React, { useCallback, useContext, useMemo } from "react";
import { injectIntl } from "react-intl";
import { FixtureDetailsContext } from "../../../Config/FixtureDetails";
import GridTableDisplay from "../../Base/Shared/GridTableDisplay";
import TeamSetupCheckbox from "./TeamSetupCheckbox";

const NONE_OPTION = "";
const HandballOfficialRoles = ["official_a", "official_b", "official_c", "official_d", "official_e"];

const Staff = ({ intl, teamData, updateStaffData }) => {
  const { sport } = useContext(FixtureDetailsContext);
  const { formatMessage } = intl;

  const columns = useMemo(() => {
    return [
      formatMessage({
        id: "person.role",
        defaultMessage: "Role",
      }),
      formatMessage({
        id: "person.playing",
        defaultMessage: "Playing",
      }),
      formatMessage({
        id: "person.name",
        defaultMessage: "Name",
      }),
    ];
  }, [formatMessage]);

  const possibleStaffRoles = useMemo(() => {
    if (sport === "basketball") {
      return ["coach", "assistantCoach1", "assistantCoach2"];
    }
    if (sport === "hockey") {
      return ["coach", "manager", "physiotherapist", "doctor", "other"];
    }

    if (sport === "handball") {
      return [...HandballOfficialRoles];
    }
    // by default only return coach as assignable staff role
    return ["coach"];
  }, [sport]);

  const getCoaches = useCallback(
    (currentRole) => {
      if (teamData) {
        return teamData.staff
          .filter((staffPerson) => staffPerson?.role.includes("COACH"))
          .map((staffPerson) => (
            <option key={`coach-${currentRole}-${staffPerson.personId}`} value={staffPerson.personId}>
              {staffPerson.name}
            </option>
          ));
      }
    },
    [teamData],
  );

  const getAllSeasonStaff = useCallback(() => {
    if (teamData) {
      return teamData.staff.map((staffPerson) => (
        <option key={`staff-${staffPerson.personId}`} value={staffPerson.personId}>
          {staffPerson.name}
        </option>
      ));
    }
  }, [teamData]);

  const handlePropertyChange = useCallback(
    (propertyName, person, roleName, e) => {
      const newPerson = { ...person };
      if (propertyName === "active") {
        newPerson.active = e.target.checked;
      }
      if (propertyName === "personId") {
        newPerson.personId = e.target.value;
      }
      if (newPerson.personId === "") {
        newPerson.active = false;
      }
      updateStaffData(newPerson, person, roleName);
    },
    [updateStaffData],
  );

  const rows = useMemo(() => {
    const getRoleOptions = (roleName) => {
      // Field Hockey and Handball expose every season staff member in each role dropdown so an operator can assign a
      // person to a match-day role that differs from their season role (SDC-2761 mirrors Handball SDC-2251).
      if (["hockey", "handball"].includes(sport)) {
        return getAllSeasonStaff();
      } else if (roleName.toUpperCase().includes("COACH")) {
        return getCoaches(roleName);
      }

      return null;
    };

    return possibleStaffRoles.map((roleName) => {
      const staffPerson = teamData.staffState?.[roleName]?.personId
        ? teamData.staffState[roleName]
        : { personId: NONE_OPTION, active: false };

      if (staffPerson.personId !== NONE_OPTION) {
        const person = teamData.staff.find((p) => p.personId === staffPerson.personId);
        staffPerson.confirmed = person.confirmed || false;
      } else {
        staffPerson.confirmed = false;
      }

      return [
        <span>
          {formatMessage({
            id: `role.${roleName}`,
            defaultMessage: `role.${roleName}`,
          })}
        </span>,
        <span className={"bib-background playing-" + staffPerson.active + " confirmed-" + staffPerson.confirmed}>
          <TeamSetupCheckbox
            checked={staffPerson.active}
            onChange={(e) => handlePropertyChange("active", staffPerson, roleName, e)}
            enabled={staffPerson.personId !== ""}
          />
        </span>,

        <select
          value={staffPerson.personId}
          onChange={(e) => handlePropertyChange("personId", staffPerson, roleName, e)}
        >
          <option value={NONE_OPTION}></option>
          {getRoleOptions(roleName)}
        </select>,
      ];
    });
  }, [teamData, formatMessage, handlePropertyChange, possibleStaffRoles, sport, getCoaches, getAllSeasonStaff]);

  return (
    <div>
      <GridTableDisplay
        columns={columns}
        rows={rows}
        headerClass="staff-table-header"
        rowCellClass="roster-cell"
        tableKeyPrefix={`staff_${teamData?.entityId}`}
      />
    </div>
  );
};

export default injectIntl(Staff);
